import { Table, Tag, Typography } from "antd"
import React from "react"
import { useQuery } from "react-query"
import usePlatform from "contexts/platform/use-platform"
import { Link } from "react-router-dom"
import formatDate from "dateformat"
import { MoneyDisplay } from "components/display/MoneyDisplay"
import { IMoney } from "platform-client/types"
import { QuoteStateLabels } from "containers/quotes/constants"
import { QuoteState, QuoteType } from "platform-client/types/quote"
import { useConfigurationContext } from "contexts/configuration/context"
import { PlatformVersions } from "contexts/configuration/context"

const { Text } = Typography

export const Quotes: React.FC<IQuotesProps> = (props) => {
  const platform = usePlatform()
  const configuration = useConfigurationContext()

  const quotesQuery = useQuery(
    ["quotes", props.clientID],
    () => platform.policyholders.getQuotes(props.clientID),
    {
      enabled: !!props.clientID && !!platform.token
    }
  )

  const platformVersion: PlatformVersions =
    configuration?.flags?.platformVersion || "v2"

  const columns = [
    {
      title: "Created",
      dataIndex: "dateCreated",
      key: "dateCreated",
      width: "11.11%",
      render: (date: string) => formatDate(new Date(date), "dd/mm/yyyy")
    },
    {
      title: "Quote ID",
      key: "quoteReferenceID",
      width: "11.11%",
      render: (row: {
        quoteReferenceID: string
        id: string | undefined
        quoteID?: string
        quoteType: QuoteType
      }) => (
        <Link
          to={`/${
            row.quoteType === "Vri" && platformVersion === "v3"
              ? "quick-quote"
              : "quote"
          }/${row.id || row.quoteID}/summary`}
        >
          <u>{row.quoteReferenceID}</u>
        </Link>
      )
    },
    {
      title: "State",
      dataIndex: "quoteState",
      key: "quoteState",
      width: "11.11%",
      render: (quoteState: QuoteState) => {
        const labelConfig = QuoteStateLabels[quoteState]
        return (
          <Tag color={labelConfig.color}>{labelConfig.label.toUpperCase()}</Tag>
        )
      }
    },
    {
      title: "Last updated",
      dataIndex: "dateCreated",
      key: "lastUpdated",
      width: "11.11%",
      render: (date: string) => formatDate(new Date(date), "dd/mm/yyyy")
    },
    {
      title: "Name insured",
      dataIndex: ["risk", "proposer"],
      key: "nameInsured",
      width: "11.11%",
      render: (proposer: {
        personName?: { firstName: string; lastName: string }
        companyName?: string
      }) => {
        if (!proposer) return "-"
        if (proposer.companyName) return <Text>{proposer.companyName}</Text>
        if (proposer.personName)
          return (
            <Text>
              {proposer.personName.firstName} {proposer.personName.lastName}
            </Text>
          )
        return "-"
      }
    },
    {
      title: "Quote amount",
      dataIndex: "quoteGross",
      key: "quoteAmount",
      width: "11.11%",
      align: "right" as const,
      render: (quoteGross: IMoney) =>
        quoteGross ? <MoneyDisplay value={quoteGross} /> : "-"
    },
    {
      title: "Type",
      key: "quoteType",
      width: "11.11%",
      render: (quote: { quoteType: QuoteType }) =>
        configuration?.quote?.types?.[quote?.quoteType] || "-"
    },
    {
      title: "Product",
      dataIndex: ["product"],
      key: "product",
      width: "11.11%",
      render: (product: { name: string }) => (
        <Text style={{ width: "100%", display: "block" }}>{product?.name}</Text>
      )
    },
    {
      title: "Policy ID",
      key: "policyId",
      width: "11.12%",
      render: (quote: { policyReferenceID?: string; policyID?: string }) =>
        quote.policyID ? (
          <Link to={`/policy/${quote.policyID}`}>
            <u>{quote.policyReferenceID}</u>
          </Link>
        ) : (
          "-"
        )
    }
  ]

  return (
    <Table
      loading={quotesQuery.isLoading}
      dataSource={quotesQuery.data || []}
      columns={columns}
      rowKey="id"
      pagination={{ pageSize: 10 }}
      locale={{
        emptyText: "No quotes found"
      }}
    />
  )
}

export interface IQuotesProps {
  clientID: string
}
